// frontend/src/services/tokenManager.js
// Token 管理服務 - 處理自動刷新
// 注意：Refresh Token 現在儲存在 HttpOnly Cookie 中，不再使用 localStorage

// 刷新配置常數
const REFRESH_CONFIG = {
  maxRetries: 3, // 最大重試次數
  baseDelay: 1000, // 基礎延遲 1 秒（指數退避）
  preRefreshTime: 600000, // 提前 10 分鐘刷新
  minWaitTime: 60000, // 最少等待 60 秒
};

class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.refreshPromise = null;
    this.isRefreshing = false;
    this.subscribers = [];
    this.refreshSubscribers = []; // 等待刷新完成的請求隊列

    // Token 存儲鍵 (只有 Access Token 存在 localStorage)
    this.ACCESS_TOKEN_KEY = 'accessToken';
    this.TOKEN_EXPIRES_KEY = 'tokenExpires';
    // Refresh Token 現在由 HttpOnly Cookie 管理，不再使用 localStorage

    // 初始化時設定自動刷新
    this.setupAutoRefresh();

    // 設定 Visibility API 處理器（Tab 回前景時檢查 Token）
    this.setupVisibilityHandler();
  }

  /**
   * 儲存 Access Token (Refresh Token 由 HttpOnly Cookie 管理)
   * @param {string} accessToken - Access Token
   * @param {number} expiresIn - 過期時間（秒），預設 7200 秒（2 小時）
   */
  saveTokens(accessToken, expiresIn = 7200) {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);

    // 計算過期時間
    const expiresAt = new Date(Date.now() + expiresIn * 1000);
    localStorage.setItem(this.TOKEN_EXPIRES_KEY, expiresAt.toISOString());

    // 重新設定自動刷新
    this.setupAutoRefresh();
  }

  /**
   * 取得 Access Token
   */
  getAccessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * 檢查是否可能有 Refresh Token（Cookie 由瀏覽器管理，無法直接檢查）
   * 透過嘗試刷新來確認
   */
  hasRefreshToken() {
    // HttpOnly Cookie 無法從 JavaScript 讀取
    // 只能透過嘗試刷新來確認是否有效
    return true; // 假設有，讓刷新請求去驗證
  }

  /**
   * 檢查 Token 是否即將過期
   * 直接解析 JWT 中的 exp 而非依賴 localStorage
   */
  isTokenExpiringSoon() {
    const accessToken = this.getAccessToken();
    if (!accessToken) return true;

    try {
      const payload = this.parseJwt(accessToken);
      if (!payload || !payload.exp) return true;

      const expiresAt = payload.exp * 1000; // JWT exp 是秒，轉為毫秒
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // 如果已過期或剩餘時間少於預設時間（10 分鐘），視為即將過期
      return timeUntilExpiry < REFRESH_CONFIG.preRefreshTime;
    } catch (error) {
      console.error('[TokenManager] 檢查 Token 過期時發生錯誤:', error);
      return true; // 無法解析時，假設即將過期
    }
  }

  /**
   * 設定自動刷新
   */
  setupAutoRefresh() {
    // 清除現有計時器
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const accessToken = this.getAccessToken();

    // 只檢查 Access Token（Refresh Token 在 Cookie 中）
    if (!accessToken) {
      return;
    }

    try {
      // 解析 JWT Token（不驗證簽名，只取 payload）
      const payload = this.parseJwt(accessToken);
      if (!payload || !payload.exp) {
        return;
      }

      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // 在過期前 10 分鐘刷新（但至少等待 60 秒）
      const refreshTime = Math.max(
        REFRESH_CONFIG.minWaitTime,
        timeUntilExpiry - REFRESH_CONFIG.preRefreshTime
      );

      console.log('[TokenManager] Token 將在', Math.round(timeUntilExpiry / 1000), '秒後過期');
      console.log('[TokenManager] 計劃在', Math.round(refreshTime / 1000), '秒後刷新');

      this.refreshTimer = setTimeout(() => {
        console.log('[TokenManager] 執行自動刷新');
        this.refreshAccessToken();
      }, refreshTime);
    } catch (error) {
      console.error('[TokenManager] 設定自動刷新失敗:', error);
    }
  }

  /**
   * 訂閱刷新完成事件（等待隊列機制）
   * 當已有刷新請求進行中時，新請求會加入此隊列等待
   */
  subscribeTokenRefresh() {
    return new Promise((resolve, reject) => {
      this.refreshSubscribers.push({ resolve, reject });
    });
  }

  /**
   * 通知所有等待刷新的請求
   * @param {string|null} token - 新的 Access Token，如果刷新失敗則為 null
   * @param {Error|null} error - 如果刷新失敗，則為錯誤物件
   */
  onRefreshed(token, error = null) {
    this.refreshSubscribers.forEach((subscriber) => {
      if (error) {
        subscriber.reject(error);
      } else {
        subscriber.resolve(token);
      }
    });
    this.refreshSubscribers = [];
  }

  /**
   * 刷新 Access Token
   * 使用隊列機制確保並發請求只會發送一個刷新請求
   * 包含自動重試機制（指數退避）
   */
  async refreshAccessToken() {
    // 如果已經在刷新中，加入等待隊列
    if (this.isRefreshing) {
      console.log('[TokenManager] 已有刷新請求進行中，加入等待隊列');
      return this.subscribeTokenRefresh();
    }

    // 開始刷新流程
    this.isRefreshing = true;

    try {
      // 使用重試機制包裹刷新邏輯
      const result = await this._retryWithBackoff(() => this._doRefresh());
      // 通知所有等待的請求
      this.onRefreshed(result);
      this.notifySubscribers(result);
      return result;
    } catch (error) {
      console.error('[TokenManager] Token 刷新最終失敗:', error);

      // 通知所有等待的請求刷新失敗
      this.onRefreshed(null, error);

      // 暫時性錯誤（503 DB 忙碌）：保留 Token，不登出，讓用戶下次操作再重試
      if (error.transient) {
        console.warn('[TokenManager] 暫時性錯誤，保留 Token 不登出');
        throw error;
      }

      // 認證錯誤：清除 Token 並重定向到登入頁面
      this.clearTokens();
      window.location.href = '/login';

      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * 帶指數退避的重試機制
   * @param {Function} fn - 要執行的異步函數
   * @param {number} maxRetries - 最大重試次數
   * @returns {Promise} 執行結果
   */
  async _retryWithBackoff(fn, maxRetries = REFRESH_CONFIG.maxRetries) {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        // 如果錯誤標記為不重試（如 401/403），直接拋出
        if (error.noRetry) {
          throw error;
        }
        console.warn(`[TokenManager] 刷新嘗試 ${attempt + 1}/${maxRetries} 失敗:`, error.message);
        // 最後一次嘗試不需要等待
        if (attempt < maxRetries - 1) {
          const delay = REFRESH_CONFIG.baseDelay * Math.pow(2, attempt);
          console.log(`[TokenManager] ${delay / 1000} 秒後重試...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }
    console.error(`[TokenManager] 所有 ${maxRetries} 次重試都失敗`);
    throw lastError;
  }

  /**
   * 實際刷新邏輯（單次請求）
   * Refresh Token 透過 HttpOnly Cookie 自動發送
   * 注意：此方法由 refreshAccessToken 呼叫，重試邏輯在那裡處理
   */
  async _doRefresh() {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // 重要：攜帶 Cookie
      headers: {
        'Content-Type': 'application/json',
      },
      // 不需要 body，Refresh Token 在 Cookie 中
    });

    // 401/403 錯誤不應重試（Token 無效或權限不足）
    if (response.status === 401 || response.status === 403) {
      const error = new Error(`認證失敗: ${response.status}`);
      error.noRetry = true; // 標記為不重試
      throw error;
    }

    if (!response.ok) {
      // 503 = 伺服器暫時忙碌（DB I/O 錯誤等），重試後仍不應登出
      const error = new Error(`刷新失敗: ${response.status}`);
      if (response.status === 503) {
        error.transient = true;
      }
      throw error;
    }

    const result = await response.json();

    if (result.success && result.data) {
      const { accessToken, expiresIn = 7200 } = result.data; // 預設 2 小時

      // 儲存新 Access Token（Refresh Token 由 Cookie 管理）
      this.saveTokens(accessToken, expiresIn);

      console.log('[TokenManager] Token 刷新成功');
      return accessToken;
    } else {
      throw new Error('無效的刷新回應');
    }
  }

  /**
   * 訂閱 Token 更新事件
   */
  subscribe(callback) {
    this.subscribers.push(callback);

    // 返回取消訂閱函數
    return () => {
      this.subscribers = this.subscribers.filter((sub) => sub !== callback);
    };
  }

  /**
   * 通知訂閱者
   */
  notifySubscribers(newToken) {
    this.subscribers.forEach((callback) => {
      try {
        callback(newToken);
      } catch (error) {
        console.error('[TokenManager] 訂閱者回調錯誤:', error);
      }
    });
  }

  /**
   * 清除所有本地 Token（Cookie 由後端清除）
   */
  clearTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRES_KEY);
    // 注意：Refresh Token Cookie 由後端登出 API 清除

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * 解析 JWT Token
   */
  parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('[TokenManager] JWT 解析失敗:', error);
      return null;
    }
  }

  /**
   * 登入處理
   * @param {string} accessToken - Access Token
   * @param {number} expiresIn - 過期時間（秒），預設 7200（2 小時）
   * 注意：Refresh Token 由後端設定為 HttpOnly Cookie
   */
  handleLogin(accessToken, expiresIn = 7200) {
    this.saveTokens(accessToken, expiresIn);
  }

  /**
   * 登出處理
   * 呼叫後端 API 清除 Cookie 並撤銷 Token
   */
  async handleLogout() {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include', // 重要：攜帶 Cookie 讓後端清除
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('[TokenManager] 登出 API 呼叫失敗:', error);
    }

    // 清除本地 Access Token
    this.clearTokens();
  }

  /**
   * 檢查是否已登入
   * 只檢查 Access Token（Refresh Token 在 HttpOnly Cookie 中無法直接檢查）
   */
  isAuthenticated() {
    const accessToken = this.getAccessToken();
    // 只檢查 Access Token 是否存在
    // 如果 Access Token 過期但有有效的 Refresh Token Cookie，刷新機制會自動處理
    return !!accessToken;
  }

  /**
   * 設定 Visibility API 處理器
   * 當使用者切換回此 Tab 時，檢查 Token 是否即將過期
   * 這可以防止使用者長時間離開後回來時 Token 已過期
   */
  setupVisibilityHandler() {
    // 檢查瀏覽器是否支援 Visibility API
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
      console.warn('[TokenManager] Visibility API 不支援');
      return;
    }

    document.addEventListener('visibilitychange', () => {
      // 只在頁面變為可見時處理
      if (document.visibilityState === 'visible') {
        console.log('[TokenManager] Tab 回到前景，檢查 Token 狀態');

        // 如果 Token 即將過期，主動刷新
        if (this.isTokenExpiringSoon()) {
          console.log('[TokenManager] Token 即將過期，主動刷新');
          this.refreshAccessToken().catch((error) => {
            console.error('[TokenManager] Visibility 觸發的刷新失敗:', error);
          });
        } else {
          // 重新設定自動刷新計時器（因為 Tab 在背景時計時器可能不準確）
          this.setupAutoRefresh();
        }
      }
    });

    console.log('[TokenManager] Visibility API 處理器已設定');
  }
}

// 匯出單例
const tokenManager = new TokenManager();
export default tokenManager;
